<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength, email } from '@vuelidate/validators';
import { useBranding } from 'shared/composables/useBranding';
import FormInput from '../../../../components/Form/Input.vue';
import { resetPassword } from '../../../../api/auth';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Testimonials from '../../signup/components/Testimonials/Index.vue';
import { mapGetters } from 'vuex';

export default {
  components: { FormInput, NextButton, Testimonials },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { v$: useVuelidate(), replaceInstallationName };
  },
  data() {
    return {
      credentials: { email: '' },
      resetPassword: {
        message: '',
        showLoading: false,
      },
      error: '',
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    isAEasyDeskInstance() {
      return (this.globalConfig?.installationName || '') === 'EasyDesk';
    },
  },
  validations() {
    return {
      credentials: {
        email: {
          required,
          email,
          minLength: minLength(4),
        },
      },
    };
  },
  methods: {
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.resetPassword.showLoading = false;
      useAlert(message);
    },
    submit() {
      this.resetPassword.showLoading = true;
      resetPassword(this.credentials)
        .then(res => {
          let successMessage = this.$t('RESET_PASSWORD.API.SUCCESS_MESSAGE');
          if (res.data && res.data.message) {
            successMessage = res.data.message;
          }
          this.showAlertMessage(successMessage);
        })
        .catch(error => {
          let errorMessage = this.$t('RESET_PASSWORD.API.ERROR_MESSAGE');
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          this.showAlertMessage(errorMessage);
        });
    },
  },
};
</script>

<template>
  <main
    class="relative min-h-screen overflow-hidden bg-gradient-to-r from-n-amber-3 via-white to-white dark:from-n-background dark:via-n-solid-2 dark:to-n-solid-3"
  >
    <div
      class="pointer-events-none absolute -top-40 left-1/2 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-n-amber-5/50 blur-[160px] dark:bg-n-brand/10"
    />
    <div
      class="pointer-events-none absolute bottom-[-14rem] right-[-6rem] h-96 w-[34rem] rounded-full bg-n-amber-7/40 blur-[180px] dark:bg-n-blue-5/20"
    />

    <div class="relative flex min-h-screen flex-col-reverse lg:flex-row">
      <Testimonials v-if="isAEasyDeskInstance" class="hidden flex-1 lg:flex" />

      <section
        class="relative flex flex-1 items-center justify-center px-6 py-16 sm:px-10 lg:px-20"
      >
        <div class="relative z-10 w-full max-w-lg">
          <div class="mb-12 space-y-8">
            <div class="flex items-center gap-3">
              <img
                :src="globalConfig.logo"
                :alt="globalConfig.installationName"
                class="block h-9 w-auto shrink-0 dark:hidden"
              />
              <img
                v-if="globalConfig.logoDark"
                :src="globalConfig.logoDark"
                :alt="globalConfig.installationName"
                class="hidden h-9 w-auto shrink-0 dark:block"
              />
            </div>
            <span
              class="inline-flex items-center gap-2 rounded-full bg-n-amber-4 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-n-amber-11 shadow-sm ring-1 ring-n-amber-5/60"
            >
              <span class="i-lucide-rocket h-3.5 w-3.5" />
              {{ $t('RESET_PASSWORD.INTRO_BADGE') }}
            </span>

            <div class="space-y-4">
              <h1 class="text-4xl font-bold leading-tight text-n-slate-12">
                {{ $t('RESET_PASSWORD.TITLE') }}
              </h1>
              <p class="text-base text-n-slate-11">
                {{ replaceInstallationName($t('RESET_PASSWORD.DESCRIPTION')) }}
              </p>
            </div>
          </div>

          <div
            class="relative overflow-hidden rounded-3xl border border-n-amber-4 bg-white/95 p-8 shadow-[0_32px_96px_-48px_rgba(255,106,0,0.65)] backdrop-blur-xl ring-1 ring-n-amber-4/70 transition-all duration-300 dark:border-n-container/30 dark:bg-n-solid-2/90 dark:ring-n-container sm:p-10"
          >
            <div
              class="pointer-events-none absolute inset-x-10 -top-24 h-32 rounded-full bg-n-amber-5/50 blur-3xl dark:bg-n-brand/5"
            />
            <div class="relative space-y-6">
              <form class="space-y-5" @submit.prevent="submit">
                <FormInput
                  v-model="credentials.email"
                  name="email_address"
                  :has-error="v$.credentials.email.$error"
                  :error-message="$t('RESET_PASSWORD.EMAIL.ERROR')"
                  :placeholder="$t('RESET_PASSWORD.EMAIL.PLACEHOLDER')"
                  @input="v$.credentials.email.$touch"
                />
                <NextButton
                  lg
                  amber
                  type="submit"
                  data-testid="submit_button"
                  class="w-full"
                  :label="$t('RESET_PASSWORD.SUBMIT')"
                  :disabled="
                    v$.credentials.email.$invalid || resetPassword.showLoading
                  "
                  :is-loading="resetPassword.showLoading"
                />
              </form>

              <div class="text-center text-sm text-n-slate-11">
                {{ $t('RESET_PASSWORD.GO_BACK_TO_LOGIN') }}
                <router-link
                  to="/app/login"
                  class="inline-flex items-center justify-center gap-2 rounded-full bg-n-amber-3 px-4 py-2 font-medium text-n-amber-11 shadow-sm ring-1 ring-inset ring-n-amber-5 transition hover:-translate-y-0.5 hover:bg-n-amber-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-amber-8 focus-visible:ring-offset-2 dark:bg-n-solid-3 dark:text-n-slate-12 dark:ring-n-container dark:hover:bg-n-alpha-2"
                >
                  <span class="i-lucide-arrow-left h-4 w-4" />
                  {{ $t('REGISTER.CTA_LOGIN') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>
